/**
 * responsive.ts — the ONE shared responsive contract for the app.
 *
 * Two rendering worlds share these exact breakpoints so the whole product
 * behaves consistently at every width:
 *   • CSS-class web UI (the app shell + `.web.tsx` screens) is driven by the
 *     media queries in `theme.css`, keyed to the SAME px values below.
 *   • React-Native-StyleSheet screens (admin pages, ticket detail, etc.) use
 *     the `useResponsive()` hook here to stack rows, scale padding and switch
 *     column counts.
 *
 * Breakpoints (kept identical to theme.css):
 *   mobile  : width < 768
 *   tablet  : 768 ≤ width < 1200
 *   desktop : width ≥ 1200
 */
import { useMemo } from 'react';
import { useBreakpoint, type Breakpoint } from '../hooks/useBreakpoint';

export type { Breakpoint };

/** Raw px edges — the single source of truth, mirrored in theme.css. */
export const BREAKPOINTS = { mobile: 768, tablet: 1200 } as const;

export interface Responsive {
  breakpoint: Breakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  width: number;
  /** Horizontal page gutter that shrinks on small screens. */
  pagePadX: number;
  /** Vertical page gutter. */
  pagePadY: number;
  /** Gap between cards / form fields. */
  gap: number;
  /** Max content width for centered reading columns (null = full width). */
  contentMaxWidth: number | null;
  /** How many columns a responsive grid should use right now. */
  columns: number;
  /**
   * Data-table rendering mode. Real `<table>` only on desktop, where wide
   * columns fit; on mobile + tablet rows collapse into stacked label/value
   * cards so nothing is clipped or sideways-scrolled.
   */
  tableMode: 'table' | 'cards';
  /**
   * Two-pane layouts (content + side panel) stack vertically below desktop so
   * the side panel never overflows the viewport.
   */
  sidePanelStacks: boolean;
  /**
   * Pick a value by breakpoint with graceful fallback
   * (tablet falls back to mobile, desktop to tablet, then mobile).
   */
  select: <T>(opts: { mobile: T; tablet?: T; desktop?: T }) => T;
}

export function useResponsive(): Responsive {
  const { breakpoint, isMobile, isTablet, isDesktop, width } = useBreakpoint();

  return useMemo(() => {
    const select = <T,>({ mobile, tablet, desktop }: { mobile: T; tablet?: T; desktop?: T }): T => {
      if (isDesktop) return (desktop ?? tablet ?? mobile);
      if (isTablet) return (tablet ?? mobile);
      return mobile;
    };

    return {
      breakpoint,
      isMobile,
      isTablet,
      isDesktop,
      width,
      pagePadX: select({ mobile: 20, tablet: 32, desktop: 40 }),
      pagePadY: select({ mobile: 20, tablet: 24, desktop: 28 }),
      gap: select({ mobile: 12, tablet: 16, desktop: 20 }),
      contentMaxWidth: select({ mobile: null, tablet: 880, desktop: 880 }),
      columns: select({ mobile: 1, tablet: 2, desktop: 2 }),
      tableMode: isDesktop ? 'table' : 'cards',
      sidePanelStacks: !isDesktop,
      select,
    };
  }, [breakpoint, isMobile, isTablet, isDesktop, width]);
}
